/**
 * Network LLM usage — aggregates from GET /networks/:id/usage (metering DynamoDB).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getRestApiBearerToken } from '@/services/apiToken';
import { Tab } from '@headlessui/react';
import { motion } from 'framer-motion';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import BudgetAllocationPanel from '@stepflow/ui-shared/components/BudgetAllocationPanel.jsx';
import Spinner from '@stepflow/ui-shared/components/Spinner.jsx';
import { formatCompactNumber } from '@stepflow/ui-shared/utils/formatCompactNumber.js';
import MemoryVectorDbPanel from './MemoryVectorDbPanel';
import PlatformBillingPanel, {
  RefreshesOnLoadBadge,
  StorageGaugeDailyBadge,
} from './PlatformBillingPanel';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

// Compact "K" formatting (1,245,238 → "1,245K") — one shared house rule so every
// usage/billing surface reads the same. See utils/formatCompactNumber.js.
function formatInt(n) {
  return formatCompactNumber(n);
}

/** Points are the primary rolled-up unit (USAGE-AND-BUDGET.md §7a); 1 pt = $0.001. */
function formatPoints(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${formatCompactNumber(n)} pts`;
}

function formatUsd(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const v = Number(n);
  if (v < 0.01 && v > 0) return `<$0.01`;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(v);
}

/** Readable label for platform / legacy metering rows */
function formatAgentDisplayName(agentId) {
  const id = (agentId || '').trim();
  if (id === 'system') return 'Other (legacy)';
  if (id === 'unknown') return 'Unattributed';
  return id || '—';
}

export default function UsageTab({ network }) {
  const { user } = useAuth();
  const networkId = network?.networkId;
  const [months, setMonths] = useState(6);
  /** workspace = exclude all attribution-slice rows (no double-count).
   *  byRoom = #ROOM# (agentic room) slice. byPool = #POOL# (customer pool) slice. */
  const [llmScope, setLlmScope] = useState('workspace');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchUsage = useCallback(async () => {
    if (!networkId || networkId === 'new') return;
    setLoading(true);
    setError(null);
    try {
      const idToken = getRestApiBearerToken();
      const q = new URLSearchParams({ months: String(months) });
      if (llmScope === 'byPool' || llmScope === 'byRoom') {
        q.set('view', llmScope);
      }
      const response = await fetch(`${API_BASE_URL}/networks/${networkId}/usage?${q}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.error || body.message || response.statusText);
      }
      setData(body);
    } catch (e) {
      setError(e.message || 'Failed to load usage');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [networkId, user?.idToken, months, llmScope]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const totals = data?.totals;
  const range = data?.range;
  const meteringNote = data?.meteringNote;
  const meteringScope = data?.meteringScope;
  const byModel = data?.byModel || [];
  const byAgent = data?.byAgent || [];
  const byPool = data?.byPool || [];
  const byRoom = data?.byRoom || [];
  const rawAgentNetwork = data?.byAgentNetwork;
  const rawAgentSystem = data?.byAgentSystem;

  const { byAgentNetwork, byAgentSystem, systemMergedIntoNetwork } = useMemo(() => {
    const agents = byAgent;
    // When every row is platform (brain, embeddings, …), API returns byAgentNetwork: []. Do not treat
    // that as "missing split" — only fall back to combined byAgent if the API omits byAgentNetwork (legacy).
    const net =
      rawAgentNetwork === undefined
        ? agents
        : Array.isArray(rawAgentNetwork)
          ? rawAgentNetwork
          : [];
    const sysRaw = Array.isArray(rawAgentSystem) ? rawAgentSystem : [];
    const fingerprint = (rows) =>
      (rows || [])
        .map(
          (r) =>
            `${String(r.agentId || '').trim()}|${Number(r.inputTokens) || 0}|${Number(r.outputTokens) || 0}|${Number(r.estimatedCost) || 0}`
        )
        .sort()
        .join(';');
    const netFp = fingerprint(net);
    const sys =
      sysRaw.length > 0 && fingerprint(sysRaw) !== netFp ? sysRaw : [];
    const merged = sysRaw.length > 0 && sys.length === 0 && net.length > 0;
    return {
      byAgentNetwork: net,
      byAgentSystem: sys,
      systemMergedIntoNetwork: merged,
    };
  }, [byAgent, rawAgentNetwork, rawAgentSystem]);

  const monthly = data?.monthly || [];

  const maxMonthCost = useMemo(() => {
    let m = 0;
    monthly.forEach((row) => {
      const c = Number(row.estimatedCost) || 0;
      if (c > m) m = c;
    });
    return m || 1;
  }, [monthly]);

  const isRoomView = llmScope === 'byRoom' || meteringScope === 'agentic_room';
  const isPoolView = llmScope === 'byPool' || meteringScope === 'customer_pool';
  /** Either attribution slice (room or customer pool) — both render via PoolUsageTable. */
  const isSliceView = isRoomView || isPoolView;
  const sliceRows = isRoomView ? byRoom : byPool;
  const sliceLabel = isRoomView ? 'room' : 'customer pool';
  /** Network Settings → Show metering details on Usage (default off). */
  const showMeteringHelp = network?.runtimeConfig?.usageMeteringHelpEnabled === true;

  if (!networkId || networkId === 'new') {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50/80 p-8">
        <p className="text-gray-600 text-sm max-w-md text-center">
          Save your network from the Build tab first. Usage is tracked per network once you run agents and LLM calls.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-8 pb-12">
        <Tab.Group>
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Usage</h2>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              {showMeteringHelp ? (
                <>
                  LLM spend; <strong className="font-medium text-gray-700">Vector DB (managed)</strong> pairs pipeline charts
                  (refreshed when you load or reload) with metering where <strong className="font-medium text-gray-700">byte</strong>{' '}
                  totals update about <strong className="font-medium text-gray-700">once per day</strong>.{' '}
                  <strong className="font-medium text-gray-700">App &amp; buffer</strong> follows the same pattern for app S3
                  and Dynamo buffer bytes.
                </>
              ) : (
                <>LLM, vector, and platform usage for this network.</>
              )}
            </p>
            <Tab.List className="mt-4 flex flex-wrap gap-1 rounded-lg bg-slate-200/70 p-1 w-fit max-w-full">
              {[
                { label: 'LLM tokens', title: 'Token and cost metering (Bedrock)' },
                {
                  label: 'Vector DB (managed)',
                  title: 'Live ingest/search pipeline (queues, Lambdas) and managed vector-index capacity & compute metering',
                },
                {
                  label: 'App & buffer',
                  title: 'App-data S3, DynamoDB buffer estimate, REST/SFN/doc-processor infra rollups',
                },
                {
                  label: 'Budget',
                  title: 'Allocate this network’s points budget across its rooms',
                },
              ].map(({ label, title }) => (
                <Tab
                  key={label}
                  title={title}
                  className={({ selected }) =>
                    `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                      selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {label}
                </Tab>
              ))}
            </Tab.List>
          </motion.div>

          <Tab.Panels>
            <Tab.Panel>
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-6">
                <div className="flex flex-wrap gap-1 rounded-lg bg-slate-200/70 p-1 w-fit">
                  {[
                    { id: 'workspace', label: 'Workspace usage' },
                    { id: 'byRoom', label: 'By room' },
                    // 'byPool' (customer pool) removed — same attribution as 'By room'
                    // under the current convention, so it was a confusing duplicate.
                  ].map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setLlmScope(id)}
                      className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                        llmScope === id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <label
                    htmlFor="usage-months"
                    className="text-xs font-medium text-gray-500 uppercase tracking-wide"
                  >
                    Range
                  </label>
                  <select
                    id="usage-months"
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 shadow-sm focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                  >
                    <option value={3}>Last 3 months</option>
                    <option value={6}>Last 6 months</option>
                    <option value={12}>Last 12 months</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {isSliceView ? `Usage by ${sliceLabel}` : 'LLM tokens & cost'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {showMeteringHelp ? (
                      <>
                        {isRoomView
                          ? 'Attributed to the agentic room (networkId, poolId). Matches Dynamo SK #ROOM# rows only.'
                          : isPoolView
                            ? 'Attributed to Run payload pool slug (tenantId / customerId). Matches Dynamo SK #POOL# rows only.'
                            : 'Workspace-level Bedrock metering (excludes attribution-slice duplicate rows).'}
                        {range?.from && range?.to ? (
                          <span className="text-gray-400">
                            {' '}
                            · {range.from} → {range.to}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {isSliceView
                          ? `Usage by ${sliceLabel} for the selected range.`
                          : 'LLM usage for this network.'}
                        {range?.from && range?.to ? (
                          <span className="text-gray-400">
                            {' '}
                            · {range.from} → {range.to}
                          </span>
                        ) : null}
                      </>
                    )}
                  </p>
                  {showMeteringHelp && meteringNote && !isSliceView ? (
                    <p className="text-xs text-amber-800/90 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mt-2 max-w-3xl">
                      {meteringNote}
                    </p>
                  ) : null}
                </div>
              </div>

              {error && (
                <div className="mb-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              {loading && !data ? (
                <Spinner variant="panel" size="lg" label="Loading usage…" />
              ) : (
                <>
                  {isSliceView ? (
                    <>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                        {[
                          { label: 'Points', value: formatPoints(totals?.points), sub: `${isRoomView ? 'Room' : 'Pool'} rows · ≈ ${formatUsd(totals?.estimatedCost)}` },
                          { label: 'Total tokens', value: formatInt(totals?.totalTokens), sub: 'input + output' },
                          { label: 'Input tokens', value: formatInt(totals?.inputTokens), sub: 'prompt' },
                          { label: 'Output tokens', value: formatInt(totals?.outputTokens), sub: 'completion' },
                        ].map((k) => (
                          <div
                            key={k.label}
                            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                          >
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                            <p className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{k.value}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>
                          </div>
                        ))}
                      </div>
                      {monthly.length > 0 && (
                        <div className="mb-10 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                          <h3 className="text-sm font-semibold text-gray-800 mb-4">{isRoomView ? 'Room' : 'Pool'}-attributed cost by month</h3>
                          <div className="flex items-end gap-1 sm:gap-2 h-40 pl-1">
                            {monthly.map((row) => {
                              const cost = Number(row.estimatedCost) || 0;
                              const h = maxMonthCost > 0 ? Math.max(8, (cost / maxMonthCost) * 100) : 8;
                              return (
                                <div
                                  key={row.month}
                                  className="flex-1 min-w-0 h-full flex flex-col items-center justify-end group"
                                >
                                  <div
                                    className="w-full max-w-[48px] mx-auto rounded-t-md bg-gradient-to-t from-teal-600 to-teal-400 opacity-90 group-hover:opacity-100 transition-opacity"
                                    style={{ height: `${h}%` }}
                                    title={`${row.month}: ${formatUsd(cost)}`}
                                  />
                                  <span className="text-[10px] sm:text-xs text-gray-500 mt-2 truncate w-full text-center">
                                    {row.month.slice(5)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <PoolUsageTable
                        rows={sliceRows}
                        slugNoun={isRoomView ? 'room' : 'customer pool'}
                        formatAgentLabel={formatAgentDisplayName}
                        detailedHelp={showMeteringHelp}
                      />
                    </>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
                        {[
                          { label: 'Points', value: formatPoints(totals?.points), sub: `≈ ${formatUsd(totals?.estimatedCost)}` },
                          { label: 'Total tokens', value: formatInt(totals?.totalTokens), sub: 'input + output' },
                          { label: 'Input tokens', value: formatInt(totals?.inputTokens), sub: 'prompt' },
                          { label: 'Output tokens', value: formatInt(totals?.outputTokens), sub: 'completion' },
                        ].map((k) => (
                          <div
                            key={k.label}
                            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                          >
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                            <p className="text-2xl font-semibold text-gray-900 mt-1 tabular-nums">{k.value}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>
                          </div>
                        ))}
                      </div>

                      {monthly.length > 0 && (
                        <div className="mb-10 rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
                          <h3 className="text-sm font-semibold text-gray-800 mb-4">Cost by month</h3>
                          <div className="flex items-end gap-1 sm:gap-2 h-40 pl-1">
                            {monthly.map((row) => {
                              const cost = Number(row.estimatedCost) || 0;
                              const h = maxMonthCost > 0 ? Math.max(8, (cost / maxMonthCost) * 100) : 8;
                              return (
                                <div
                                  key={row.month}
                                  className="flex-1 min-w-0 h-full flex flex-col items-center justify-end group"
                                >
                                  <div
                                    className="w-full max-w-[48px] mx-auto rounded-t-md bg-gradient-to-t from-blue-600 to-blue-400 opacity-90 group-hover:opacity-100 transition-opacity"
                                    style={{ height: `${h}%` }}
                                    title={`${row.month}: ${formatUsd(cost)}`}
                                  />
                                  <span className="text-[10px] sm:text-xs text-gray-500 mt-2 truncate w-full text-center">
                                    {row.month.slice(5)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-xs text-gray-400 mt-3 text-center">
                            Bar height is relative to the highest-cost month in this range.
                          </p>
                        </div>
                      )}

                      <Tab.Group>
                        <Tab.List className="flex gap-1 rounded-lg bg-gray-100/80 p-1 w-fit mb-4">
                          {['By model', 'By agent'].map((label) => (
                            <Tab
                              key={label}
                              className={({ selected }) =>
                                `rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                                  selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                                }`
                              }
                            >
                              {label}
                            </Tab>
                          ))}
                        </Tab.List>
                        <Tab.Panels>
                          <Tab.Panel>
                            <UsageTable
                              rows={byModel}
                              columns="model"
                              emptyCopy="No model usage in this period yet."
                            />
                          </Tab.Panel>
                          <Tab.Panel>
                            <div className="space-y-10">
                              <div>
                                <h3 className="text-sm font-semibold text-gray-800 mb-2">Network agents</h3>
                                {showMeteringHelp ? (
                                  <p className="text-xs text-gray-500 mb-3">
                                    Runtime agents in this network (workbench / router).
                                  </p>
                                ) : (
                                  <div className="mb-3" />
                                )}
                                <UsageTable
                                  rows={byAgentNetwork}
                                  columns="agent"
                                  emptyCopy="No network agent usage in this period yet."
                                  formatAgentLabel={formatAgentDisplayName}
                                />
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-gray-800 mb-2">System</h3>
                                {showMeteringHelp ? (
                                  <p className="text-xs text-gray-500 mb-3">
                                    Brain pipeline (Architect, PromptEngineer, …), embeddings, memory summarization, and legacy
                                    unattributed calls.
                                  </p>
                                ) : (
                                  <div className="mb-3" />
                                )}
                                {systemMergedIntoNetwork ? (
                                  showMeteringHelp ? (
                                    <p className="rounded-xl border border-gray-100 bg-slate-50/80 px-4 py-3 text-sm text-gray-600">
                                      Pipeline usage matches <strong className="font-medium text-gray-800">Network agents</strong>{' '}
                                      above (same rows and totals). When metering splits network vs system scopes, both tables will
                                      differ.
                                    </p>
                                  ) : (
                                    <p className="rounded-xl border border-gray-100 bg-slate-50/80 px-4 py-3 text-sm text-gray-600">
                                      Same totals as <strong className="font-medium text-gray-800">Network agents</strong> above.
                                    </p>
                                  )
                                ) : (
                                  <UsageTable
                                    rows={byAgentSystem}
                                    columns="agent"
                                    emptyCopy="No system usage in this period yet."
                                    formatAgentLabel={formatAgentDisplayName}
                                  />
                                )}
                              </div>
                            </div>
                          </Tab.Panel>
                        </Tab.Panels>
                      </Tab.Group>
                    </>
                  )}
                </>
              )}
            </Tab.Panel>

            <Tab.Panel className="space-y-12">
              <section>
                <div className="flex flex-wrap items-center gap-2 gap-y-1">
                  <h3 className="text-base font-semibold text-gray-900">Live pipeline &amp; health</h3>
                  <RefreshesOnLoadBadge />
                </div>
                <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                  Charts and KPIs load when you open this tab or click Refresh—<strong className="font-medium text-gray-600">not</strong> on
                  the daily storage snapshot schedule. Shared environment metrics; pair with capacity &amp; metering below.
                </p>
                <div className="mt-5">
                  <MemoryVectorDbPanel network={network} layout="nested" />
                </div>
              </section>
              <section className="border-t border-gray-200 pt-10">
                <div className="flex flex-wrap items-center gap-2 gap-y-1">
                  <h3 className="text-base font-semibold text-gray-900">Capacity &amp; metering</h3>
                  <StorageGaugeDailyBadge />
                </div>
                <p className="text-sm text-gray-500 mt-1 max-w-3xl">
                  Vector-index <strong className="font-medium text-gray-600">byte totals</strong> use a scheduled job (~once per
                  day). <strong className="font-medium text-gray-600">Compute rollups</strong> below update as usage is
                  metered (usually within minutes).
                </p>
                <div className="mt-5">
                  <PlatformBillingPanel
                    network={network}
                    visibleTabIds={['vectorDb']}
                    suppressOuterTitle
                  />
                </div>
              </section>
            </Tab.Panel>

            <Tab.Panel>
              <div className="flex flex-wrap items-center gap-2 gap-y-1 mb-1">
                <h3 className="text-base font-semibold text-gray-900">App data, buffer &amp; general compute</h3>
                <StorageGaugeDailyBadge />
              </div>
              <p className="text-sm text-gray-500 mb-6 max-w-3xl">
                App S3 and DynamoDB buffer <strong className="font-medium text-gray-600">byte snapshots update daily</strong>.
                REST, Step Functions, and related <strong className="font-medium text-gray-600">compute rollups</strong>{' '}
                track usage as it happens (not on the daily storage cadence).
              </p>
              <PlatformBillingPanel
                network={network}
                visibleTabIds={['general', 'buffer']}
                suppressOuterTitle
              />
            </Tab.Panel>

            <Tab.Panel>
              {/* Allocate this network's points budget across its rooms
                  (USAGE-AND-BUDGET.md §7b). */}
              <BudgetAllocationPanel
                scope="network"
                id={networkId}
                childNoun="room"
                title="Budget by room"
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  );
}

function PoolUsageTable({ rows, formatAgentLabel, detailedHelp, slugNoun = 'customer pool' }) {
  const isRoom = slugNoun === 'room';
  if (!rows?.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500 max-w-xl mx-auto">
        {detailedHelp && !isRoom ? (
          <>
            No pool-attributed usage in this range. Enable <strong className="font-medium text-gray-700">Customer pool</strong>{' '}
            in Settings, then use <strong className="font-medium text-gray-700">tenantId</strong> or{' '}
            <strong className="font-medium text-gray-700">customerId</strong> on the Run payload (customer pool slug; not workspace
            TN-…).
          </>
        ) : (
          `No ${slugNoun}-attributed usage in this range.`
        )}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {rows.map((pool) => {
        const agents = Array.isArray(pool.byAgent) ? pool.byAgent : [];
        const tin = pool.inputTokens ?? 0;
        const tout = pool.outputTokens ?? 0;
        return (
          <div
            key={pool.poolSlug || '—'}
            className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden"
          >
            <div className="px-4 py-3 bg-slate-50/80 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2">
              <code className="text-sm font-semibold text-gray-900">{pool.poolSlug || '—'}</code>
              <div className="text-xs text-gray-500 tabular-nums">
                {formatInt(tin)} in · {formatInt(tout)} out · {formatUsd(pool.estimatedCost)}
              </div>
            </div>
            {agents.length > 0 ? (
              <details className="group">
                <summary className="px-4 py-2 text-xs font-medium text-blue-700 cursor-pointer hover:bg-slate-50/80 flex items-center gap-2">
                  <span className="inline-block transition-transform group-open:rotate-90">▸</span>
                  By agent ({agents.length})
                </summary>
                <div className="px-2 pb-3">
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-3 py-2">Agent</th>
                          <th className="px-3 py-2 text-right">Input</th>
                          <th className="px-3 py-2 text-right">Output</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2 text-right">Est. cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {agents.map((a, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-800">
                              {formatAgentLabel ? formatAgentLabel(a.agentId) : a.agentId}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatInt(a.inputTokens)}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{formatInt(a.outputTokens)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              {formatInt(a.totalTokens ?? (a.inputTokens || 0) + (a.outputTokens || 0))}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{formatUsd(a.estimatedCost)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function UsageTable({ rows, columns, emptyCopy, formatAgentLabel }) {
  if (!rows.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-12 text-center text-sm text-gray-500">
        {emptyCopy}
      </div>
    );
  }

  const showCategory = columns === 'model';

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100 bg-gray-50/80 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            {showCategory && <th className="px-4 py-3">Category</th>}
            <th className="px-4 py-3">{columns === 'model' ? 'Model' : 'Agent'}</th>
            <th className="px-4 py-3 text-right">Input</th>
            <th className="px-4 py-3 text-right">Output</th>
            <th className="px-4 py-3 text-right">Total</th>
            <th className="px-4 py-3 text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const isModel = columns === 'model';
            const label = isModel
              ? row.modelId || row.modelLabel || '—'
              : formatAgentLabel
                ? formatAgentLabel(row.agentId)
                : row.agentId;
            const categoryCell = isModel ? row.modelLabel || '—' : null;
            const tin = row.inputTokens ?? 0;
            const tout = row.outputTokens ?? 0;
            const tt = row.totalTokens ?? tin + tout;
            const cost = row.estimatedCost ?? 0;
            return (
              <tr key={i} className="border-b border-gray-50 hover:bg-slate-50/60">
                {showCategory && (
                  <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{categoryCell}</td>
                )}
                <td className="px-4 py-3">
                  <div
                    className={`font-medium text-gray-900 ${isModel ? 'truncate max-w-md' : ''}`}
                    title={isModel ? String(label) : undefined}
                  >
                    {label}
                  </div>
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(tin)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-700">{formatInt(tout)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-gray-900 font-medium">
                  {formatInt(tt)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-700 font-medium">
                  {formatUsd(cost)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="bg-slate-50/90 font-semibold text-gray-900">
            {showCategory && <td className="px-4 py-3" />}
            <td className="px-4 py-3">Total</td>
            <td className="px-4 py-3 text-right tabular-nums">
              {formatInt(rows.reduce((s, r) => s + (r.inputTokens || 0), 0))}
            </td>
            <td className="px-4 py-3 text-right tabular-nums">
              {formatInt(rows.reduce((s, r) => s + (r.outputTokens || 0), 0))}
            </td>
            <td className="px-4 py-3 text-right tabular-nums">
              {formatInt(rows.reduce((s, r) => s + (r.totalTokens ?? (r.inputTokens || 0) + (r.outputTokens || 0)), 0))}
            </td>
            <td className="px-4 py-3 text-right tabular-nums text-emerald-800">
              {formatUsd(rows.reduce((s, r) => s + (r.estimatedCost || 0), 0))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
